import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { graphql, useFragment } from 'react-relay';
import { AuctionItemSubscription } from 'dibs-auctions/exports/AuctionItemSubscription';

// types
import { PdpSharedAuctionSubscription_item$key as Item } from './__generated__/PdpSharedAuctionSubscription_item.graphql';

type Props = {
    item: Item;
    refetch: () => void;
};

type RelayVariables = {
    userId: string;
    itemId: string;
    userCountryCode: string;
    userZipCode: string;
    priceBookName: string;
    isTrade: boolean;
    afterDisplay: boolean;
    fetchUser: boolean;
    fetchAuction: boolean;
};

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
};

const subscription = graphql`
    subscription PdpSharedAuctionSubscription(
        $input: AuctionItemUpdateSubscriptionInput!
        $isTrade: Boolean
        $userId: String = ""
        $afterDisplay: Boolean!
        $fetchUser: Boolean!
        $fetchAuction: Boolean = false
        $userCountryCode: String = ""
        $userZipCode: String = ""
        $priceBookName: String = ""
        $fdfQuoteABTestVariant: String
    ) {
        auctionItemUpdate(input: $input) {
            item {
                ...PdpSharedAuctionInfo_item
            }
        }
    }
`;

const itemFragment = graphql`
    fragment PdpSharedAuctionSubscription_item on Item {
        ...AuctionItemSubscription_item @arguments(page: pdp)
    }
`;

export const PdpSharedAuctionSubscription: FC<Props> = ({ refetch, item: itemRef }) => {
    const item = useFragment(itemFragment, itemRef);

    const {
        afterDisplay,
        userId,
        isTrade,
        itemId,
        fetchUser,
        fetchAuction,
        userCountryCode,
        userZipCode,
        priceBookName,
    } = useSelector<ReduxStoreType, RelayVariables>(
        relay => relay?.relayVariables?.variables || {}
    );

    const subscriptionConfig = useMemo(
        () => ({
            subscription,
            variables: {
                userId,
                isTrade,
                afterDisplay,
                fetchUser,
                fetchAuction,
                userCountryCode,
                userZipCode,
                priceBookName,
                input: {
                    itemId,
                },
            },
        }),
        [
            afterDisplay,
            userId,
            isTrade,
            itemId,
            fetchUser,
            fetchAuction,
            userCountryCode,
            userZipCode,
            priceBookName,
        ]
    );

    return (
        <AuctionItemSubscription
            afterDisplay={afterDisplay}
            refetch={refetch}
            item={item}
            subscriptionConfig={subscriptionConfig}
        />
    );
};
